<!--
@component
BracketMatchCard

Renders the two team rows (with flags, names, scores and winner marks)
that fill a single match cell in the visual bracket.

Per-match display values (labels, scores, winner) are derived from a
`MatchSummary` so the same source of truth backs both this card and the
accessible-table fallback. The "team name → bracket phrase → TBD" choice
lives in `content.ts` and arrives here as `summary.homeLabel`/`awayLabel`,
so the markup only needs to know whether a confirmed `teamName` exists in
order to switch styling between a normal name and a "pending" placeholder.
-->

<script lang="ts">
  import Flag from '$lib/components/common/Flag/index.svelte';
  import LiveBug from '$lib/components/common/LiveBug/index.svelte';
  import { isPlaying, type Match } from '$utils/getMatchStats';
  import { hasNumericScore, type MatchSummary } from '../content';
  import TrophyIcon from './TrophyIcon.svelte';

  type Side = 'home' | 'away';
  type TeamRow = {
    side: Side;
    /** Pre-resolved display label from `content.ts`. */
    label: string;
    /** Confirmed team name, or undefined when the slot is still TBD. */
    teamName: string | undefined;
    goals: number | undefined;
    penalty: number | undefined;
    hasScore: boolean;
    isWinner: boolean;
  };

  let {
    widthRect,
    heightRect,
    match,
    round,
    summary,
  }: {
    widthRect: number;
    heightRect: number;
    match: Match;
    round: string;
    summary: MatchSummary;
  } = $props();

  const TROPHY_SIZE = 24;

  let isLive = $derived(isPlaying(match));

  // Build the two team rows from the shared summary. `label` already encodes
  // the "team name → bracket phrase → TBD" fallback (see `getTeamLabel` in
  // content.ts), so the markup just renders `row.label` and switches styles
  // based on whether a real team name is set.
  let rows = $derived<TeamRow[]>([
    {
      side: 'home',
      label: summary.homeLabel,
      teamName: summary.home?.name,
      goals: summary.homeScore?.score,
      penalty: summary.homeScore?.pen,
      hasScore: hasNumericScore(summary.homeScore),
      isWinner: summary.winner === 'home',
    },
    {
      side: 'away',
      label: summary.awayLabel,
      teamName: summary.away?.name,
      goals: summary.awayScore?.score,
      penalty: summary.awayScore?.pen,
      hasScore: hasNumericScore(summary.awayScore),
      isWinner: summary.winner === 'away',
    },
  ]);
</script>

<div
  class="match-rect relative"
  class:isLive
  style:width={`${widthRect}px`}
  style:height={`${heightRect}px`}
>
  {#if isLive}
    <div class="live-container">
      <LiveBug />
    </div>
  {/if}

  {#each rows as row (row.side)}
    <div class="team-info-box flex items-center">
      <div class="flag-container">
        <Flag country={row.teamName} width={'var(--theme-font-size-base)'} />
      </div>

      <div
        class="team-name flex items-center fml-1"
        class:winner={row.isWinner}
      >
        <span class:phrase={!row.teamName}>{row.label}</span>

        {#if row.isWinner}
          <span class="winner-icon flex items-center fml-1">
            {#if round === 'Final'}
              <TrophyIcon size={TROPHY_SIZE} />
            {:else}
              ✔
            {/if}
          </span>
        {/if}
      </div>

      {#if row.hasScore}
        <div class="score flex items-center" class:winner={row.isWinner}>
          {row.goals}
          {#if row.penalty}
            <span class="pen text-xs ml-0.5">({row.penalty})</span>
          {/if}
        </div>
      {/if}
    </div>
  {/each}
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  .live-container {
    @include mixins.fpx-2;
    position: absolute;
    top: -0.65rem;
    left: 50%;
    transform: translateX(-50%);
  }

  .match-rect {
    fill: var(--theme-colour-muted-grey);
    background-color: var(--theme-colour-muted-grey);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border: 1px solid var(--theme-colour-muted-grey);
    border-radius: calc(0.5 * var(--theme-style-border-radius));
    @include mixins.fpx-3;
    @include mixins.fpy-2;

    &.isLive {
      @include mixins.hard-shadow;

      &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 0;
        top: 0;
        border-radius: calc(0.5 * var(--theme-style-border-radius));
        mix-blend-mode: multiply;
        width: 100%;
        height: 100%;
        @include mixins.loading;
      }

      // Live score colour, scoped via parent rather than a redundant class.
      .score {
        color: var(--theme-colour-live-red);
      }
    }
  }

  .team-info-box {
    display: flex;
    align-items: center;
    position: relative;

    @include mixins.text-sm;

    .winner-icon {
      color: var(--theme-colour-trophy-gold);
    }

    .team-name {
      @include mixins.leading-none;
      &.winner {
        @include mixins.font-bold;
      }

      .phrase {
        @include mixins.text-secondary;
        @include mixins.text-xs;
      }
    }

    .flag-container {
      width: var(--theme-font-size-base);
      aspect-ratio: 3 / 2;
      outline: 1px solid var(--theme-colour-white);
    }

    .score {
      position: absolute;
      right: 0;
      &.winner {
        @include mixins.font-bold;
      }
    }
  }
</style>
